// Copyright (c) StrangeBee 2023

import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import { useGenericQuery } from '@Queries/genericQueries';
import { useCallback, useEffect, useState } from 'react';
import { ForgotPasswordConfigurationOutput, MailerConfigurationOutput } from 'thehive-sdk';
import { emptyStringToNull } from './utils';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { NAME_KEY } from './constant';

export const useSMTPConfigForm = () => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const [configStarted, setConfigStarted] = useState<Record<string, any>>({});
    const [isTls, setIsTls] = useState(false);
    const [valuePort, setValuePort] = useState(25);

    const { data: mailerConfig } = useGenericQuery<MailerConfigurationOutput>({
        fetchCallback: () => client.MailerController.getConfig(),
        key: ['get-mailer-config'],
    });

    const { data: forgotPasswordConfig } = useGenericQuery<ForgotPasswordConfigurationOutput>({
        fetchCallback: () => client.ForgetPasswordController.getConfig(),
        key: ['get-forgot-password-config'],
    });

    const cleanConfiguration = useCallback((state: Record<string, any>): Record<string, any> => {
        state.ssl = false;
        state.tls = false;

        if (state.security === 'ssl') {
            state.ssl = true;
            state.tlsRequired = false;
        }
        if (state.security === 'tls') {
            state.tls = true;
        }
        if (state.security === 'none') {
            state.tlsRequired = false;
        }

        delete state.security;
        delete state.expiration;

        return state;
    }, []);

    const onFinishHandler = useCallback(
        (state: any) => {
            const treatForm = cleanConfiguration(emptyStringToNull(state));

            const treatFormExpiration = treatForm.expiration;

            Promise.all([
                client.MailerController.edit({ ...mailerConfig, ...treatForm }),
                client.ForgetPasswordController.edit({
                    ...forgotPasswordConfig,
                    expiration: treatFormExpiration,
                }),
            ])
                .then(() => {
                    openMessage('success', i('smtp.message.success'));
                })
                .catch(handleNotificationError);

            setFieldsTouched(false);
        },
        [forgotPasswordConfig, i, mailerConfig, setFieldsTouched, cleanConfiguration],
    );

    const onChangeForm = useCallback(() => {
        onFormValuesChange();
        setFieldsTouched(true);
    }, [onFormValuesChange, setFieldsTouched]);

    const onChangePort = useCallback((value: any) => {
        setValuePort(value);
    }, []);

    const onChangeSecurity = useCallback(
        (value: string) => {
            setIsTls(value === 'tls');
            const formDataClone = form.getFieldsValue(true);

            if (value === 'ssl') {
                if (valuePort === 25) {
                    onChangePort(465);
                    form.setFieldsValue({ ...formDataClone, [NAME_KEY.port]: 465 });
                }
            } else {
                if (valuePort === 465) {
                    onChangePort(25);
                    form.setFieldsValue({ ...formDataClone, [NAME_KEY.port]: 25 });
                }
            }
        },
        [form, onChangePort, valuePort],
    );

    const onResetForm = useCallback(() => {
        const { ssl, tls } = configStarted;
        const security = ssl ? 'ssl' : tls ? 'tls' : 'none';
        setIsTls(security === 'tls');

        setFieldsTouched(false);
        form.resetFields();
    }, [configStarted, form, setFieldsTouched]);

    const handleTestConfiguration = useCallback(() => {
        const values = cleanConfiguration(emptyStringToNull({ ...configStarted, ...form.getFieldsValue(true) }));

        client.MailerController.test(values as MailerConfigurationOutput)
            .then(({ to }) =>
                openMessage('success', i('smtp.test.success.message', { email: to }), i('smtp.test.success.title')),
            )
            .catch((err) => handleNotificationError(err));
    }, [i, form, configStarted, cleanConfiguration]);

    useEffect(() => {
        form.resetFields();
    }, [configStarted]); // eslint-disable-line

    useEffect(() => {
        if (mailerConfig && forgotPasswordConfig) {
            const { expiration } = forgotPasswordConfig;
            const { ssl, tls, port, tlsRequired, ...rest } = mailerConfig;
            let portTmp = port;
            let tlsRequiredTmp = tlsRequired;
            const security = ssl ? 'ssl' : tls ? 'tls' : 'none';
            onChangeSecurity(security);

            if (security === 'ssl') {
                portTmp = port === 25 ? 465 : port;
                tlsRequiredTmp = false;
            }

            setValuePort(portTmp);
            setIsTls(security === 'tls');

            setConfigStarted({ ...rest, security, port: portTmp, expiration, tlsRequired: tlsRequiredTmp });
        }
    }, [mailerConfig, forgotPasswordConfig]); //eslint-disable-line

    return {
        form,
        onChangeForm,
        onResetForm,
        initialValues: configStarted,
        onFinishForm: onFinishHandler,
        isValid,
        fieldsTouched,
        isTls,
        onChangePort,
        onChangeSecurity,
        handleTestConfiguration,
    };
};
